import algoliasearch from 'algoliasearch/lite'

(function () {
  "use strict";

  // Get defaults from CampsUtils
  const defaults = window.CampsUtils?.defaults || {};

  const CampsNearYouCamp = {
    // Configuration (uses CampsUtils.defaults)
    config: {
      fetchCount: 5, // Fetch 5 camps to skip first one (current camp)
      displayCount: 4, // Show 4 camps (skip first, show 2-5)
      maxDistanceForDisplay: defaults.maxDistanceForDisplay,
      algoliaAppId: defaults.algoliaAppId,
      algoliaSearchKey: defaults.algoliaSearchKey,
      algoliaIndex: defaults.algoliaIndex,
    },

    // DOM elements
    elements: {
      container: null,
      list: null,
    },

    // Algolia index
    index: null,

    // Camp data from attributes
    campData: {
      lat: null,
      lng: null,
      sportSlug: null,
    },

    /**
     * Initialize the component
     */
    init: function () {
      this.elements.container = document.querySelector(
        '.rd-camps-near-you[data-page="camp"]'
      );
      if (!this.elements.container) return;

      this.elements.list = this.elements.container.querySelector(
        ".rd-camps-near-you__list"
      );

      // Read data attributes
      this.campData.lat = parseFloat(
        this.elements.container.dataset.campLat
      );
      this.campData.lng = parseFloat(
        this.elements.container.dataset.campLng
      );
      this.campData.sportSlug = this.elements.container.dataset.campSport;

      // Validate coordinates
      if (!this.campData.lat || !this.campData.lng) {
        console.error(
          "[CampsNearYouCamp] Invalid coordinates - lat:",
          this.campData.lat,
          "lng:",
          this.campData.lng
        );
        this.handleError();
        return;
      }

      console.log(
        "[CampsNearYouCamp] Initializing with coordinates:",
        this.campData.lat,
        this.campData.lng,
        "excluding sport:",
        this.campData.sportSlug
      );

      this.observeAndLoad();
    },

    /**
     * Defer Algolia request until the section enters the viewport.
     * Saves Algolia operations for users who never scroll to the bottom of the camp page.
     */
    observeAndLoad: function () {
      if (!("IntersectionObserver" in window)) {
        this.waitForAlgoliaAndLoad();
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              observer.disconnect();
              this.waitForAlgoliaAndLoad();
              break;
            }
          }
        },
        { rootMargin: "200px 0px", threshold: 0 }
      );

      observer.observe(this.elements.container);
    },

    /**
     * Wait for Algolia library to load, then initialize
     */
    waitForAlgoliaAndLoad: function () {
      const maxAttempts = 50; // 5 seconds max
      let attempts = 0;

      const checkAlgolia = () => {
        if (typeof algoliasearch !== "undefined") {
          const client = algoliasearch(
            this.config.algoliaAppId,
            this.config.algoliaSearchKey,
          )
          this.index = client.initIndex(this.config.algoliaIndex)
          this.loadCamps()
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(checkAlgolia, 100);
        } else {
          console.error(
            "CampsNearYouCamp: Algolia library failed to load after 5 seconds"
          );
          this.handleError();
        }
      };

      checkAlgolia();
    },

    /**
     * Load camps near the current camp location
     */
    loadCamps: async function () {
      try {
        const hits = await this.fetchNearbyCamps();

        if (hits.length > 1) {
          // Skip first camp (likely current camp) and show next 4 (camps 2-5)
          const campsToShow = hits.slice(1, this.config.fetchCount);
          console.log(
            `[CampsNearYouCamp] Found ${hits.length} camps, showing ${campsToShow.length} (skipping first one)`
          );
          this.renderCamps(campsToShow);
        } else {
          console.log(
            "[CampsNearYouCamp] Not enough camps found from other sports"
          );
          this.handleNoCamps();
        }
      } catch (error) {
        console.error("[CampsNearYouCamp] ERROR:", error);
        this.handleError();
      }
    },

    /**
     * Fetch nearby camps from different sports
     * Note: Fetches 5 camps, first one will be skipped (likely current camp)
     */
    fetchNearbyCamps: async function () {
      const filters = [
        "sessionsCount > 0", // Only camps with active sessions
        `NOT sportSlug:"${this.campData.sportSlug}"`, // Exclude current sport
      ].join(" AND ");

      const { hits } = await this.index.search("", {
        aroundLatLng: `${this.campData.lat},${this.campData.lng}`,
        // NO aroundRadius - Algolia returns sorted by distance without limit
        hitsPerPage: this.config.fetchCount, // Fetch 5 to skip first one
        filters: filters,
        getRankingInfo: true,
      });

      return hits;
    },

    /**
     * Render camp cards to DOM
     */
    renderCamps: function (camps) {
      if (!this.elements.list) return;

      if (camps.length === 0) {
        this.handleNoCamps();
        return;
      }

      const maxDistanceMeters = this.config.maxDistanceForDisplay * 1609;

      const html = camps
        .map((camp) => {
          // Show distance only if within maxDistanceForDisplay miles. Accept 0
          // (same-venue camp); hide only when geo data is absent.
          const geo = camp._rankingInfo?.geoDistance;
          const showDistance =
            typeof geo === "number" && geo <= maxDistanceMeters;
          return window.CampsUtils?.renderCampCardHtml(camp, { showDistance });
        })
        .join("");

      this.elements.list.innerHTML = html;
      pushGTMViewItemList(camps, false, null);
      selectItemTracking(camps, null)
    },

    /**
     * Handle case when no camps found
     */
    handleNoCamps: function () {
      if (this.elements.list) {
        this.elements.list.innerHTML =
          '<li class="rd-camps-near-you__no-results">No nearby camps found from other sports.</li>';
      }
    },

    /**
     * Handle errors - keep skeletons visible
     */
    handleError: function () {
      console.warn(
        "[CampsNearYouCamp] FLOW: Error → Keeping skeleton state"
      );
    },
  };

  // Initialize on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () =>
      CampsNearYouCamp.init()
    );
  } else {
    CampsNearYouCamp.init();
  }
})();
